import React, { useState } from 'react'
import { useMutation } from 'react-query'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import { useForm } from 'src/forms'
import * as yup from 'yup'

import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { Helmet } from 'react-helmet'

import { setApiErrors } from 'src/forms/errors'

import { skuit } from 'src/services/skuit'
import { QuerySelectField } from 'src/forms'
import { useQueryPaginator } from 'src/components/Table'
import { Box,FormGroup, FormLabel, FormControlLabel, Checkbox } from '@material-ui/core'

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  formButton: {
    margin: theme.spacing(2),
  },
}))

const kioskAuthSchema = yup.object().shape({
  facility: yup.string().required().label('Facility'),
  scopes: yup.array().of(yup.string()).min(1).label('Scopes'),
})

const scopeOptions = [
  { label: "Driver Check-In", value: "kiosk.driver_check_in" },
];

export const ManageDevicesPage = () => {
  const classes = useStyles()

  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)

  const [kioskAuth, setKioskAuth] = useState(() => skuit.auth.kioskAuth)
  const kioskIsAuthed = !!kioskAuth
  const { Form, Field, data: formData } = useForm(kioskAuthSchema)

  const authorizeKiosk = async (data: any) => {
    await skuit.auth.authorizeKiosk(data)
    setKioskAuth(skuit.auth.kioskAuth)
  }

  const revokeKiosk = async () => {
    await skuit.auth.revokeKioskAuth()
    setKioskAuth(skuit.auth.kioskAuth)
  }

  const { isLoading, mutate: authenticateKiosk } = useMutation(
    authorizeKiosk,
    {
      onSuccess: () => {
        toast.success(() => 'Successfully authenticated kiosk!')
      },
      onError: (error: AxiosError) => {
        setApiErrors(formData.setError, error)
      },
    }
  )

  return  <>
      <Helmet title="Manage Devices" />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="h3" gutterBottom>
            Manage Devices
          </Typography>
          <Divider className={classes.divider} />

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Kiosk</Typography>
              <Paper>
                <Grid container spacing={6}>
                  <Grid item md={8}>
                    <Form onSubmit={authenticateKiosk}>
                      <QuerySelectField
                        name="facility"
                        label="Facility"
                        paginator={facilityPaginator}
                      />
                      <Box mt={3}>
                        <Field
                          name="scopes"
                          defaultValue={kioskAuth?.scopes || []}
                          renderField={({ name, value, setValue, error }) => {
                            const handleCheckboxChange = (scopeValue: string) => {
                              setValue(
                                name,
                                value.includes(scopeValue)
                                  ? value.filter((v: string) => v !== scopeValue)
                                  : [...value, scopeValue]
                              );
                            };

                            return (
                              <FormGroup>
                                <FormLabel component="legend">Scopes</FormLabel>
                                {scopeOptions.map((option) => (
                                  <FormControlLabel
                                    key={option.value}
                                    control={
                                      <Checkbox
                                        checked={(value || []).includes(option.value)}
                                        onChange={() => handleCheckboxChange(option.value)}
                                        disabled={kioskIsAuthed}
                                      />
                                    }
                                    label={option.label}
                                  />
                                ))}
                                {error && <Typography color="error">{error}</Typography>}
                              </FormGroup>
                            );
                          }}
                        />
                      </Box>
                      { kioskIsAuthed && <Box>Expires: {kioskAuth?.expiresOn.toLocaleString() || "-"}</Box>}
                      <Button
                        className={classes.formButton}
                        disabled={isLoading}
                        type="submit"
                        color="primary"
                        variant="contained"
                      >
                        {kioskIsAuthed ? 'Renew' : 'Authenticate'}
                      </Button>
                      {kioskIsAuthed && (
                        <Button
                          className={classes.formButton}
                          disabled={isLoading}
                          type="button"
                          color="secondary"
                          variant="text"
                          onClick={revokeKiosk}
                        >
                          Revoke
                        </Button>
                      )}
                    </Form>
                  </Grid>
                </Grid>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
}
